import React, { useMemo } from 'react';
import { SlRow, useStacklineTheme, hexToRgba } from '@stackline/ui';
import Box from '@mui/material/Box';
import { SplineChartV2, SplineChartV2Series } from 'src/components/BeaconV2Components/SplineChartV2';
import { timestampToWeekId } from 'src/utils/dateUtils';
import { Text } from '../Generic/Text';
import { SlColumn } from 'src/components/BeaconV2Components/Generic/SlColumn';
import {
  Direction,
  getColorForMetricChangeValue,
  getIconForMetricChangeForDirection
} from 'src/components/BeaconV2Components/utils/chartStyles';
import {
  BEACON_PRO_LARGE_CARD_HEIGHT,
  BEACON_PRO_CARD_WIDTH,
  FORECAST_KEY_METRICS_CARD_WIDTH
} from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import { ValueOf } from 'sl-api-connector';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import colors from 'src/utils/colors';

/**
 * Props for the spline preview in the metric card
 */
export interface SplinePreviewProps {
  metricType: ValueOf<typeof METRICTYPE>;
  primaryData: [number, number][];
  secondaryData: [number, number][];
}

/**
 * Converts old [timestamp, value][] format to SplineChartV2Series format
 */
const convertToV2Series = (
  data: [number, number][],
  id: string,
  name: string
): SplineChartV2Series => ({
  id,
  name,
  data: (data || []).map(([timestamp, value]) => ({
    weekId: timestampToWeekId(timestamp),
    value
  }))
});

export interface MetricSummaryTrendCardProps {
  /**
   * Metric title, e.g. "Total Traffic"
   */
  title: string;
  /**
   * Formatted value of the metric, e.g. "13.58M" or "$11.48"
   */
  value: string;
  /**
   * Formatted percentage change. Should be an absolute value.
   * The sign is given through `direction`
   */
  change: string;
  /**
   * Primary and secondary data sets for the spline preview
   */
  splinePreviewProps: SplinePreviewProps;
  /**
   * When selected is true, shows a gray background
   */
  selected: boolean;
  /**
   * If the change is positive or negative
   */
  direction: Direction;
  onClick: () => void;
  children?: React.ReactNode;
  /**
   * When true, use neutral grey for the change arrow/text instead of green/red.
   * Used for cost metrics where higher/lower isn't inherently good/bad (e.g. Ad Spend).
   */
  useNeutralChangeColor?: boolean;
}

/**
 * The metric summary card with a spline preview for use on the Beacon summary page.
 */
const MetricSummaryTrendCard = ({
  variant,
  children,
  ...rest
}: MetricSummaryTrendCardProps & { variant: 'large' | 'small' | 'medium' }) => {
  if (variant === 'small') {
    return <MetricsSummaryTrendCardSmall {...rest} />;
  }
  return (
    <MetricSummaryTrendCardLarge variant={variant} {...rest}>
      {children}
    </MetricSummaryTrendCardLarge>
  );
};

/**
 * Large variant of the metric summary card
 */
export const MetricSummaryTrendCardLarge = ({
  title,
  value,
  change,
  splinePreviewProps,
  selected,
  direction,
  onClick,
  children,
  variant,
  useNeutralChangeColor: useNeutralProp = false
}: MetricSummaryTrendCardProps & { variant: 'large' | 'medium' }) => {
  const theme = useStacklineTheme();
  const backgroundColor = hexToRgba(theme.colors.secondaryPorcelain, 0.5);
  const isNeutral = useNeutralProp || direction === 'neutral';

  // Convert old format to SplineChartV2 series format
  const primarySeries = useMemo(
    () => convertToV2Series(splinePreviewProps.primaryData, 'primary', 'Primary'),
    [splinePreviewProps.primaryData]
  );

  const comparisonSeries = useMemo(
    () => convertToV2Series(splinePreviewProps.secondaryData, 'comparison', 'Comparison'),
    [splinePreviewProps.secondaryData]
  );

  return (
    <Box
      sx={{
        height: children ? undefined : BEACON_PRO_LARGE_CARD_HEIGHT,
        minHeight: BEACON_PRO_LARGE_CARD_HEIGHT,
        width: variant === 'large' ? BEACON_PRO_CARD_WIDTH : FORECAST_KEY_METRICS_CARD_WIDTH,
        backgroundColor: selected ? hexToRgba(theme.colors.secondaryPorcelain, 0.5) : undefined,
        cursor: 'pointer',
        '&:hover': {
          backgroundColor
        },
        borderRadius: theme.spacing.sm
      }}
      onClick={onClick}
      role="button"
    >
      <SlColumn spacing="sm" horizontalInset="md" verticalInset="md">
        <Text variant="subtitle3" color="secondary" transform="uppercase">
          {title}
        </Text>
        <SlRow spacing="sm" verticalPosition="center">
          <Text variant="h2">{value}</Text>
          <SlRow verticalPosition="center" spacing="xs">
            {getIconForMetricChangeForDirection(
              direction,
              isNeutral ? colors.graysSecondaryText : undefined
            )}
            <Text
              variant="subtitle3"
              color={getColorForMetricChangeValue(
                null,
                direction,
                isNeutral ? colors.graysSecondaryText : undefined
              )}
            >
              {change}
            </Text>
          </SlRow>
        </SlRow>
        <SlColumn spacing="lg">
          <SplineChartV2
            primarySeries={primarySeries}
            comparisonSeries={comparisonSeries}
            metricType={splinePreviewProps.metricType}
            preview="large"
            width={variant === 'medium' ? 210 : undefined}
          />
          {children}
        </SlColumn>
      </SlColumn>
    </Box>
  );
};

/**
 * Small variant of the metric summary card
 */
export const MetricsSummaryTrendCardSmall = ({
  title,
  value,
  change,
  splinePreviewProps,
  selected,
  direction,
  onClick,
  useNeutralChangeColor: useNeutralProp = false
}: MetricSummaryTrendCardProps) => {
  const theme = useStacklineTheme();
  const backgroundColor = hexToRgba(theme.colors.secondaryPorcelain, 0.5);
  const isNeutral = useNeutralProp || direction === 'neutral';

  // Convert old format to SplineChartV2 series format
  const primarySeries = useMemo(
    () => convertToV2Series(splinePreviewProps.primaryData, 'primary', 'Primary'),
    [splinePreviewProps.primaryData]
  );

  const comparisonSeries = useMemo(
    () => convertToV2Series(splinePreviewProps.secondaryData, 'comparison', 'Comparison'),
    [splinePreviewProps.secondaryData]
  );

  return (
    <Box
      sx={{
        height: '96px',
        width: BEACON_PRO_CARD_WIDTH,
        backgroundColor: selected ? backgroundColor : undefined,
        cursor: 'pointer',
        '&:hover': {
          backgroundColor
        },
        borderRadius: theme.spacing.sm
      }}
      onClick={onClick}
    >
      <SlColumn horizontalInset="md" style={{ paddingTop: '13px', paddingBottom: '13px' }} widths="full">
        <SlRow>
          <SlColumn>
            <Text variant="subtitle3" color="secondary" transform="uppercase">
              {title}
            </Text>
          </SlColumn>
        </SlRow>
        <SlRow verticalInset="sm" horizontalPosition="space-between">
          <SlColumn spacing="sm">
            <Text variant="body0">{value}</Text>
            <SlRow spacing="xs" verticalPosition="center">
              {getIconForMetricChangeForDirection(
                direction,
                isNeutral ? colors.graysSecondaryText : undefined
              )}
              <Text
                variant="subtitle3"
                color={getColorForMetricChangeValue(
                  null,
                  direction,
                  isNeutral ? colors.graysSecondaryText : undefined
                )}
              >
                {change}
              </Text>
            </SlRow>
          </SlColumn>
          <Box sx={{ transform: 'translateY(-32px)' }}>
            <SplineChartV2
              primarySeries={primarySeries}
              comparisonSeries={comparisonSeries}
              metricType={splinePreviewProps.metricType}
              preview="small"
            />
          </Box>
        </SlRow>
      </SlColumn>
    </Box>
  );
};

export default MetricSummaryTrendCard;
